import type { JSX } from 'react';
import { App, Empty, Skeleton, Switch } from 'antd';
import {
  AimOutlined,
  AppstoreOutlined,
  BulbOutlined,
  FileDoneOutlined,
  FireOutlined,
  InfoCircleOutlined,
  ReadOutlined,
  StarFilled,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { educationApi } from '../lib/wikiApi';
import { learnApi } from '../lib/api/learn';
import { Button, PageTitle } from '../components/ds';
import { LEVELS, initials, levelOf, pluralRu } from '../components/learn/learn-utils';
import './Learn.css';

/**
 * «Мои очки и бейджи» (/points, HUB 2.0 — PageLearn «Очки» + PageMobile):
 * уровень (Стажёр → Повар → Су-шеф → Шеф, пороги — константа фронта),
 * очки, серия дней, место в рейтинге, история начислений, бейджи с
 * условиями, рейтинг команды (только своя точка у сотрудников; участие —
 * по согласию, переключатель здесь же). Данные — gamification/mine.
 */
const BADGE_ICON: Record<string, JSX.Element> = {
  first_course: <ReadOutlined />,
  courses5: <TrophyOutlined />,
  perfect: <AimOutlined />,
  streak7: <FireOutlined />,
  ack100: <FileDoneOutlined />,
  scorm: <AppstoreOutlined />,
  qod30: <BulbOutlined />,
  mentor: <TeamOutlined />,
};

export function PointsPage(): JSX.Element {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const q = useQuery({ queryKey: ['wiki', 'gamification', 'mine', 'full'], queryFn: () => learnApi.gamification(30), staleTime: 30_000 });
  const opt = useMutation({
    mutationFn: (on: boolean) => educationApi.gamificationPrefs(on),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'gamification', 'mine'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const back = <Button variant="ghost" to="/education">К обучению</Button>;
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  const d = q.data;
  if (!d) {
    return (
      <div>
        <PageTitle title="Мои очки и бейджи" right={back} />
        <Empty description="Очки начисляются за уроки, тесты и серию дней — войдите в вики, чтобы их копить" />
      </div>
    );
  }
  const lvl = levelOf(d.points);
  const me = d.leaderboard.find((r) => r.isMe) ?? null;
  const earned = d.badges.filter((b) => b.earned).length;
  const sub = [
    lvl.next ? `до уровня «${lvl.next}» — ${lvl.toNext} ${pluralRu(lvl.toNext, 'очко', 'очка', 'очков')}` : 'максимальный уровень',
    `серия ${d.streakDays} ${pluralRu(d.streakDays, 'день', 'дня', 'дней')}`,
    me ? `${me.rank}-е место${d.scope ? ` · ${d.scope}` : ''}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div>
      <PageTitle title="Мои очки и бейджи" sub="Очки за уроки, тесты и серию дней, бейджи и место в рейтинге точки" right={back} />

      <div className="fk-learn-pts-top">
        <section className="fk-learn-dark fk-learn-pts-hero">
          <img src="/brand/illustrations/bowl.png" alt="" />
          <span className="fk-learn-star"><StarFilled /></span>
          <div style={{ position: 'relative', minWidth: 0 }}>
            <div className="fk-learn-eyebrow">Уровень · {lvl.name}</div>
            <div className="fk-learn-pts-num">
              {d.points.toLocaleString('ru-RU')}
              <span style={{ fontFamily: 'var(--fk-font-ui)', fontSize: 16, fontWeight: 600, color: 'rgba(255,255,255,.7)', marginLeft: 8 }}>{pluralRu(d.points, 'очко', 'очка', 'очков')}</span>
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.72)', marginTop: 6 }}>{sub}</div>
            <div className="fk-learn-dark-bar" style={{ marginTop: 10, maxWidth: 360, height: 6 }}><span style={{ width: `${lvl.pct}%` }} /></div>
            <div className="fk-learn-levels">
              {LEVELS.map((l, i) => <span key={l.name} data-on={i <= lvl.index}>{l.name}{i > 0 ? ` · ${l.min}` : ''}</span>)}
            </div>
          </div>
        </section>

        <section className="fk-learn-card fk-learn-pad">
          <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6 }}>История</div>
          {d.recent.length === 0 ? (
            <div className="fk-learn-muted" style={{ fontSize: 13 }}>Пока нет начислений — пройдите урок или ответьте на вопрос дня.</div>
          ) : (
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {d.recent.map((r, i) => (
                <div key={i} className="fk-learn-hist">
                  <b>{r.delta > 0 ? `+${r.delta}` : r.delta}</b>
                  <span style={{ flex: 1, minWidth: 0 }}>{r.reason}</span>
                  <span className="fk-learn-muted" style={{ whiteSpace: 'nowrap' }}>{new Date(r.at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
          <h2 className="fk-learn-h2" style={{ margin: 0 }}>Бейджи</h2>
          <span className="fk-learn-muted" style={{ fontSize: 13 }}>{earned} из {d.badges.length} получено</span>
        </div>
        <div className="fk-learn-badges">
          {d.badges.map((b) => (
            <div key={b.code} className="fk-learn-bdg" data-earned={b.earned} title={b.description}>
              <span className="fk-learn-bdg-ic">{BADGE_ICON[b.code] ?? <span>{b.icon}</span>}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{b.name}</div>
                <div className="fk-learn-muted" style={{ fontSize: 12 }}>
                  {b.earned && b.at ? `получен ${new Date(b.at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}` : b.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="fk-learn-card fk-learn-pad" style={{ marginTop: 24, maxWidth: 720 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <h2 className="fk-learn-h2" style={{ margin: 0 }}>Рейтинг команды</h2>
          {d.scope && <span className="fk-learn-muted" style={{ fontSize: 13 }}>{d.scope}</span>}
          <label style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, minHeight: 44, cursor: 'pointer' }}>
            <Switch checked={d.leaderboardOptIn} loading={opt.isPending} onChange={(v) => opt.mutate(v)} />
            Участвовать в рейтинге
          </label>
        </div>
        {!d.leaderboardOptIn ? (
          <div className="fk-learn-muted" style={{ fontSize: 13 }}>
            Вы не участвуете в рейтинге — коллеги не видят ваши очки. Включите, чтобы сравнить себя с командой.
          </div>
        ) : d.leaderboard.length === 0 ? (
          <div className="fk-learn-muted" style={{ fontSize: 13 }}>В рейтинге пока только вы — позовите коллег включить участие.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {d.leaderboard.map((r) => (
              <div key={`${r.rank}-${r.name}`} className="fk-learn-board-row" data-me={r.isMe}>
                <span className="fk-learn-mono" style={{ fontWeight: 800, color: r.rank <= 3 ? 'var(--fk-orange)' : 'var(--fk-text-muted)' }}>{r.rank}</span>
                <span className="fk-learn-ava">{initials(r.name)}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}{r.isMe ? ' (вы)' : ''}</span>
                <span className="fk-learn-mono" style={{ fontWeight: 600 }}>{r.points.toLocaleString('ru-RU')}</span>
              </div>
            ))}
          </div>
        )}
        <div className="fk-learn-muted" style={{ fontSize: 12, marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
          <InfoCircleOutlined /> Сотрудники точки соревнуются внутри своей точки; участие можно выключить в любой момент.
        </div>
      </section>
    </div>
  );
}
